import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, UserPlus, ChatCircleDots, Check, X, CircleNotch,
  Storefront, UsersThree, CalendarBlank, Briefcase, UsersFour, PencilSimple,
  Envelope, LinkSimple, Gear,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import Avatar from "../components/Avatar";
import { Header, Loading, NotFound } from "./MeetupsPage";

/**
 * PublicProfilePage — /app/u/:username
 *
 * Renders any user's public profile: basic identity, bio, skills, join date,
 * followed by tabs of their businesses, meetups, events, jobs and teams.
 * Includes Add Friend and Message CTAs (self view swaps them for Edit).
 */
export default function PublicProfilePage() {
  const { username } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setNotFound(false);
    try {
      const { data } = await api.get(`/profile/@${username}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [username]);

  const u = data?.user;

  const sendRequest = async () => {
    if (!u) return;
    setBusy(true);
    try {
      await api.post("/friends/request", { username: u.username });
      await load();
    } catch { /* ignore */ }
    setBusy(false);
  };

  const acceptRequest = async () => {
    if (!u?.friendship_id) return;
    setBusy(true);
    try { await api.post(`/friends/accept/${u.friendship_id}`); await load(); } catch { /* ignore */ }
    setBusy(false);
  };

  const declineRequest = async () => {
    if (!u?.friendship_id) return;
    setBusy(true);
    try { await api.post(`/friends/decline/${u.friendship_id}`); await load(); } catch { /* ignore */ }
    setBusy(false);
  };

  const unfriend = async () => {
    if (!u) return;
    if (!window.confirm(`Unfriend @${u.username}?`)) return;
    setBusy(true);
    try { await api.delete(`/friends/${u.user_id}`); await load(); } catch { /* ignore */ }
    setBusy(false);
  };

  const message = () => {
    if (!u) return;
    navigate(`/app/messages?user=${u.user_id}`);
  };

  if (loading) return <div className="max-w-5xl mx-auto"><Loading/></div>;
  if (notFound || !u)
    return (
      <div className="max-w-5xl mx-auto">
        <NotFound label="User not found." backTo="/app"/>
      </div>
    );

  const memberSince = u.created_at
    ? new Date(u.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="public-profile">
      <button onClick={() => navigate(-1)}
              data-testid="profile-back"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> Back
      </button>

      {/* ── Identity card ───────────────────────────────────────── */}
      <div className="rounded-2xl p-6"
           style={{
             background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
             border: "1px solid rgba(212,175,55,0.45)",
             boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
           }}>
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <Avatar name={u.name} image={u.avatar_image} size={96}
                  ringColor="rgba(212,175,55,0.6)"
                  testId="profile-avatar"/>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
              HigherVision · Profile
            </div>
            <h1 className="mt-1 font-display text-[34px] leading-tight truncate"
                data-testid="profile-name"
                style={{
                  background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  letterSpacing: "0.02em",
                }}>
              {u.name || `@${u.username}`}
            </h1>
            <div className="mt-1 text-[13px] text-[#F2DC8C]/80" data-testid="profile-username">
              @{u.username}
              {u.is_admin && (
                <span className="ml-2 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest"
                      style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
                  Admin
                </span>
              )}
            </div>
            {u.bio && (
              <p className="mt-3 text-[13px] text-[#F2DC8C]/85 leading-relaxed max-w-2xl"
                 data-testid="profile-bio">
                {u.bio}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#F2DC8C]/70">
              {u.location && (
                <span className="inline-flex items-center gap-1" data-testid="profile-location">
                  <MapPin size={12}/> {u.location}
                </span>
              )}
              {memberSince && (
                <span className="inline-flex items-center gap-1" data-testid="profile-joined">
                  <CalendarBlank size={12}/> Joined {memberSince}
                </span>
              )}
              {u.points > 0 && (
                <span className="text-[#FFD700]" data-testid="profile-points">✦ {u.points} pts</span>
              )}
            </div>

            {/* Skills */}
            {u.skills?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="profile-skills">
                {u.skills.slice(0, 12).map((s) => (
                  <span key={s} className="px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest"
                        style={{ border: "1px solid rgba(212,175,55,0.45)", color: "#F2DC8C" }}>
                    {s}
                  </span>
                ))}
              </div>
            )}

            {/* Links */}
            {u.links?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3 text-[12px]">
                {u.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[#FFD700] hover:underline">
                    <LinkSimple size={12}/> {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2 md:min-w-[190px]">
            <ProfileCTAs
              user={u}
              busy={busy}
              onAdd={sendRequest}
              onAccept={acceptRequest}
              onDecline={declineRequest}
              onUnfriend={unfriend}
              onMessage={message}
            />
          </div>
        </div>
      </div>

      {/* ── Content grid ───────────────────────────────────────── */}
      <Section title="Businesses" icon={Storefront} count={data.businesses.length} testId="section-biz">
        {data.businesses.length === 0
          ? <EmptyRow label="No businesses yet."/>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.businesses.map((b) => (
                <Link key={b.business_id} to={`/app/businesses/${b.business_id}`}
                      data-testid={`profile-biz-${b.business_id}`}
                      className="rounded-xl overflow-hidden block hover:-translate-y-0.5 transition-transform"
                      style={tileStyle}>
                  <div className="h-16 w-full overflow-hidden"
                       style={{ background: "linear-gradient(120deg, rgba(255,215,0,0.15), rgba(212,175,55,0.05))" }}>
                    {b.banner_image && (
                      <img src={`${API}/files/${b.banner_image}`} alt="" className="w-full h-full object-cover"/>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-display text-[16px] text-[#FFD700] truncate">{b.name}</div>
                    {b.tagline && <div className="text-[11px] text-[#F2DC8C]/70 truncate mt-0.5">{b.tagline}</div>}
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">{b.category}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
      </Section>

      <Section title="Meetups Hosting" icon={UsersThree} count={data.meetups.length} testId="section-meetups">
        {data.meetups.length === 0
          ? <EmptyRow label="Not hosting any meet-ups."/>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.meetups.map((m) => (
                <Link key={m.meetup_id} to={`/app/meetups/${m.meetup_id}`}
                      data-testid={`profile-meetup-${m.meetup_id}`}
                      className="rounded-xl p-3 block hover:-translate-y-0.5 transition-transform"
                      style={tileStyle}>
                  <div className="font-display text-[16px] text-[#FFD700] truncate">{m.title}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">
                    {new Date(m.event_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </div>
                  {m.location && <div className="text-[11px] text-[#F2DC8C]/70 truncate mt-0.5">{m.location}</div>}
                </Link>
              ))}
            </div>
          )}
      </Section>

      <Section title="Events" icon={CalendarBlank} count={data.events.length} testId="section-events">
        {data.events.length === 0
          ? <EmptyRow label="No events hosted."/>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.events.map((e) => (
                <Link key={e.event_id} to={`/app/events/${e.event_id}`}
                      data-testid={`profile-event-${e.event_id}`}
                      className="rounded-xl p-3 block hover:-translate-y-0.5 transition-transform"
                      style={tileStyle}>
                  <div className="font-display text-[16px] text-[#FFD700] truncate">{e.title}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">
                    {e.starts_at && new Date(e.starts_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {e.category && <> · {e.category}</>}
                  </div>
                  {e.location && <div className="text-[11px] text-[#F2DC8C]/70 truncate mt-0.5">{e.location}</div>}
                </Link>
              ))}
            </div>
          )}
      </Section>

      <Section title="Jobs Posted" icon={Briefcase} count={data.jobs.length} testId="section-jobs">
        {data.jobs.length === 0
          ? <EmptyRow label="No jobs posted."/>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.jobs.map((j) => (
                <Link key={j.job_id} to={`/app/jobs/${j.job_id}`}
                      data-testid={`profile-job-${j.job_id}`}
                      className="rounded-xl p-3 block hover:-translate-y-0.5 transition-transform"
                      style={tileStyle}>
                  <div className="font-display text-[16px] text-[#FFD700] truncate">{j.title}</div>
                  {j.company && <div className="text-[11px] text-[#F2DC8C]/80 truncate mt-0.5">{j.company}</div>}
                  {j.location && <div className="text-[11px] text-[#F2DC8C]/60 truncate">{j.location}{j.remote && " · Remote"}</div>}
                </Link>
              ))}
            </div>
          )}
      </Section>

      <Section title="Teams" icon={UsersFour} count={data.teams.length} testId="section-teams">
        {data.teams.length === 0
          ? <EmptyRow label="Not part of any teams."/>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.teams.map((t) => (
                <Link key={t.team_id} to={`/app/teams/${t.team_id}`}
                      data-testid={`profile-team-${t.team_id}`}
                      className="rounded-xl p-3 block hover:-translate-y-0.5 transition-transform"
                      style={tileStyle}>
                  <div className="font-display text-[16px] text-[#FFD700] truncate">{t.name}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">
                    {(t.member_ids?.length || 0)} member{(t.member_ids?.length || 0) === 1 ? "" : "s"}
                    {t.owner_id === u.user_id && " · Owner"}
                  </div>
                </Link>
              ))}
            </div>
          )}
      </Section>
    </div>
  );
}

const tileStyle = {
  background: "linear-gradient(180deg, rgba(26,18,6,0.65) 0%, rgba(5,3,0,0.85) 100%)",
  border: "1px solid rgba(212,175,55,0.35)",
};

function ProfileCTAs({ user, busy, onAdd, onAccept, onDecline, onUnfriend, onMessage }) {
  const s = user.friend_status;
  if (s === "self") {
    return (
      <Link to="/app/settings"
            data-testid="profile-edit-btn"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em]"
            style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
        <PencilSimple size={12} weight="bold"/> Edit Profile
      </Link>
    );
  }
  const primaryBtn = "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-50";
  const goldBg = { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" };
  const ghostBg = { border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" };
  return (
    <>
      {s === "none" && (
        <button onClick={onAdd} disabled={busy} data-testid="profile-add-friend-btn"
                className={primaryBtn} style={goldBg}>
          {busy ? <CircleNotch size={12} className="animate-spin"/> : <UserPlus size={12} weight="bold"/>} Add Friend
        </button>
      )}
      {s === "outgoing" && (
        <button disabled data-testid="profile-request-sent"
                className={primaryBtn} style={ghostBg}>
          <Check size={12}/> Request Sent
        </button>
      )}
      {s === "incoming" && (
        <>
          <button onClick={onAccept} disabled={busy} data-testid="profile-accept-btn"
                  className={primaryBtn} style={goldBg}>
            <Check size={12} weight="bold"/> Accept
          </button>
          <button onClick={onDecline} disabled={busy} data-testid="profile-decline-btn"
                  className={primaryBtn} style={ghostBg}>
            <X size={12}/> Decline
          </button>
        </>
      )}
      {s === "friends" && (
        <button onClick={onUnfriend} disabled={busy} data-testid="profile-unfriend-btn"
                className={primaryBtn} style={ghostBg}>
          <Check size={12}/> Friends
        </button>
      )}
      <button onClick={onMessage} data-testid="profile-message-btn"
              className={primaryBtn}
              style={s === "friends" ? goldBg : ghostBg}>
        <ChatCircleDots size={12} weight="bold"/> Message
      </button>
    </>
  );
}

function Section({ title, icon: Icon, count, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl p-5"
             style={{
               background: "linear-gradient(180deg, rgba(26,18,6,0.7) 0%, rgba(5,3,0,0.88) 100%)",
               border: "1px solid rgba(212,175,55,0.35)",
             }}>
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <Icon size={14} weight="fill" color="#0a0703"/>
        </span>
        <div className="font-display text-[18px] text-[#FFD700]">{title}</div>
        <span className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/50">· {count}</span>
      </div>
      {children}
    </section>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="text-[12px] text-[#F2DC8C]/50 italic">{label}</div>
  );
}
